// Card: the universal card primitive.
// Composes the `aw-card aw-card--glow ... reveal` pattern used 13x across sections.
//
// Variants map to the design system's modifier classes:
//   `proj`, `pod`, `pod--host`, `post`, `talkcard`, `spotlight`, `cta`, `home`,
//   `social__card`, `skillcard`, `cal`
//
// Polymorphic: by default renders <div>. If `href` is set, renders <a> (internal
// or external). External links get target="_blank" rel="noreferrer noopener"
// and the text-decoration:none reset is applied automatically.

import type { ReactNode, CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '@lib/utils/classNames';

export type CardVariant =
  | 'proj'
  | 'pod'
  | 'pod--host'
  | 'post'
  | 'talkcard'
  | 'spotlight'
  | 'cta'
  | 'home'
  | 'social__card'
  | 'skillcard'
  | 'cal';

export interface CardProps {
  children: ReactNode;
  /** Design-system modifier(s). Use space-separated for multiple, e.g. 'pod pod--host'. */
  variant?: CardVariant | `${CardVariant} ${CardVariant}`;
  /** Apply the --glow modifier (true by default). */
  glow?: boolean;
  /** Add the reveal animation (true by default). */
  reveal?: boolean;
  /**
   * Index in a list — used to compute stagger delay `${index * 50}ms`.
   * Pass `i` from a map; the Card handles the rest.
   */
  staggerIndex?: number;
  /** If set, the Card renders as a link. */
  href?: string;
  /** Internal router link vs external. Auto-detected from href if not set. */
  external?: boolean;
  className?: string;
  style?: CSSProperties;
  id?: string;
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href) && !href.includes('alisonhaire.com');
}

const LINK_RESET_STYLE: CSSProperties = { textDecoration: 'none' };

export function Card({
  children,
  variant,
  glow = true,
  reveal = true,
  staggerIndex,
  href,
  external,
  className,
  style,
  id,
}: CardProps) {
  const className_ = cx(
    'aw-card',
    glow && 'aw-card--glow',
    variant,
    reveal && 'reveal',
    className
  );

  const styleWithDelay: CSSProperties =
    typeof staggerIndex === 'number'
      ? { ...style, transitionDelay: `${staggerIndex * 50}ms` }
      : style ?? {};

  if (href) {
    const isExternal = external ?? isExternalHref(href);
    const linkStyle: CSSProperties = { ...LINK_RESET_STYLE, ...styleWithDelay };

    if (isExternal) {
      return (
        <a
          id={id}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className={className_}
          style={linkStyle}
        >
          {children}
        </a>
      );
    }
    // Internal hashes (#anchor) stay as plain anchors; routes use <Link>.
    if (href.startsWith('#')) {
      return (
        <a id={id} href={href} className={className_} style={linkStyle}>
          {children}
        </a>
      );
    }
    return (
      <Link id={id} to={href} className={className_} style={linkStyle}>
        {children}
      </Link>
    );
  }

  return (
    <div id={id} className={className_} style={styleWithDelay}>
      {children}
    </div>
  );
}
